// Sovelluksen päivitysilmoitus: puhdas logiikka (hook: lib/useAppUpdate.js, näkymät: components/UpdateSheet.jsx).
//
// Palvelin kertoo julkaistun version (GET /api/app-version: { build, name, size }); asennettu versio tulee
// Androidilta (@capacitor/app getInfo().build = versionCode). Vertailu on pelkkä kokonaislukujen vertailu, koska
// rakennus antaa APK:lle ja julkaisulle saman luvun.

/** Aikaisintaan näin usein (ms) haetaan versiotieto uudelleen sovelluksen palatessa etualalle. */
export const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** Asennetun version luku (Capacitor antaa build-arvon merkkijonona). NaN-arvo tarkoittaa tuntematonta. */
export function installedBuild(info) {
  const n = Number.parseInt(info?.build, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Onko palvelimella uudempi versio kuin asennettu. Tuntematon asennettu versio ei koskaan ilmoita päivityksestä. */
export function updateAvailable(installed, remote) {
  const have = typeof installed === "number" ? installed : installedBuild(installed);
  return have !== null && Number(remote?.build) > have;
}

/**
 * Ladattavan tiedoston osoite: palvelimen osoite + polku. Polun antaa palvelin (`download` versiotiedossa,
 * mukana latauksen salaisuus); ilman sitä käytetään avointa oletuspolkua.
 */
export function downloadUrl(baseUrl, path) {
  const safe = typeof path === "string" && path.startsWith("/app/") ? path : "/app/tilehunting.apk";
  return `${String(baseUrl ?? "").replace(/\/+$/, "")}${safe}`;
}

/** Koko luettavasti: "5,8 Mt". */
export function sizeText(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  return `${(bytes / 1024 / 1024).toLocaleString("fi-FI", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Mt`;
}

/** Uuden version otsikkorivi: "Versio 1.17 (5,8 Mt)". */
export function releaseLine(remote) {
  const size = sizeText(remote?.size);
  return `Versio ${remote?.name ?? remote?.build}${size ? ` (${size})` : ""}`;
}

/** Kysytäänkö uudelleen: ei, jos tämä versio on jo ohitettu ("Myöhemmin"). Uudempi julkaisu kysyy taas. */
export function shouldPrompt(remoteBuild, dismissedBuild) {
  return !(Number.isFinite(dismissedBuild) && dismissedBuild >= remoteBuild);
}

/** Onko edellisestä hausta kulunut tarpeeksi pitkä aika (tai ei hakua vielä). */
export function checkDue(lastCheckMs, nowMs, interval = CHECK_INTERVAL_MS) {
  return !Number.isFinite(lastCheckMs) || nowMs - lastCheckMs >= interval;
}

/** Passin tilarivi tarkistuksen jälkeen. */
export function statusLine({ installed, remote, error }) {
  if (error) return "Päivitystä ei voitu tarkistaa. Tarkista yhteys ja yritä uudelleen.";
  if (!remote) return "";
  if (!remote.build) return "Palvelimelle ei ole julkaistu päivitystä.";
  return updateAvailable(installed, remote) ? `Uusi versio saatavilla: ${releaseLine(remote)}.` : "Sovellus on ajan tasalla.";
}
